import { Buffer } from 'buffer';
import logger from '../error/logger';
import { MATRIX_FACEBOOK_INSTALL_DECRYPT_SECRET } from '@shared/matrix/const';

interface DecodedUTMContent {
  app: number;
  t: number;
  source: {
    data: string;
    nonce: string;
  };
}

interface FacebookDecryptedData {
  ad_id: number;
  ad_objective_name: string;
  adgroup_id: number,
  adgroup_name: string;
  campaign_id: number;
  campaign_name: string;
  campaign_group_id: number;
  campaign_group_name: string;
  account_id: number;
  is_instagram: boolean;
  is_an: boolean;
  publisher_platform: string;
  platform_position: string | null;
}

interface SearchOriginData {
  search_content: string;
  source: 'search';
}

interface H5OriginData {
  source: 'h5';
  open_url: string;
  entrance_url: string;
}

interface ShareOriginData {
  source: 'share';
  share_info: string;
}

interface FacebookOriginData {
  source: 'ad';
  ad_channel: 'meta';
  ad_info: string;
}

interface UnknownOriginData {
  source: 'unknown';
}

export type OriginData = SearchOriginData | H5OriginData | ShareOriginData | FacebookOriginData | UnknownOriginData;

/**
 * 使用AES-GCM解密数据
 * @param cipherHex 加密的十六进制字符串
 * @param nonceHex nonce的十六进制字符串
 * @param keyHex 密钥的十六进制字符串
 * @returns 解密后的数据
 */
async function decryptAESGCM(cipherHex: string, nonceHex: string, keyHex: string): Promise<string> {
  const startTime = performance.now();
  
  // 转换Hex到Bytes
  const key = Buffer.from(keyHex, 'hex');
  const ciphertextWithTag = Buffer.from(cipherHex, 'hex');
  const nonce = Buffer.from(nonceHex, 'hex');

  // 分割密文和Authentication Tag
  const ciphertext = ciphertextWithTag.slice(0, -16);
  const tag = ciphertextWithTag.slice(-16);

  // 使用Web Crypto API进行解密
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    key,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
      tagLength: 128,
      additionalData: new Uint8Array(0)
    },
    cryptoKey,
    Buffer.concat([ciphertext, tag])
  );

  const endTime = performance.now();
  console.log(`AES-GCM解密耗时: ${(endTime - startTime).toFixed(2)}ms`);
  
  return new TextDecoder().decode(decrypted);
}

/**
 * 解密Google Play引荐来源数据
 * @param utmParams UTM参数字符串
 * @returns 解密后的数据
 */
export async function decodeGooglePlayReferrer(utmParamsString: string): Promise<OriginData> {
  const unknownData = {
    source: 'unknown',
  } as const;
  try {
    // 将utmParams中的所有,替换为&
    const utmParams = utmParamsString.replaceAll(',', '&');
    // 解析UTM参数
    const params = new URLSearchParams(utmParams);
    const utmTerm = params.get('utm_term');
    const utmSource = params.get('utm_source');

    // 首先判断是否来自自然搜索
    if (utmTerm) {
      return {
        source: 'search',
        search_content: utmTerm,
      }
    }

    // 然后判断是否来自H5
    if (utmSource === 'h5') {
      const open_url = params.get('open_url');
      const entrance_url = params.get('entrance_url');
      return {
        source:'h5',
        open_url: open_url || '',
        entrance_url: entrance_url || '',
      }
    }

    // 判断是否来自分享
    if (utmSource === 'share') {
      const share_info = params.get('share_info');
      return {
        source:'share',
        share_info: share_info || '',
      }
    }
  
		// 最后判断是否来自Facebook广告
		if (!['apps.instagram.com', 'apps.facebook.com'].includes(utmSource || '')) {
			return unknownData;
		}
    const utmContent = params.get('utm_content');
    
    if (!utmContent) {
      throw new Error('No utm_content found in params');
    }

    // 解码utm_content
    const decodedContent = decodeURIComponent(utmContent);
    
    // 添加调试日志
    console.log('解码后的内容:', decodedContent);
    
    let utmContentJson: DecodedUTMContent;
    try {
      utmContentJson = JSON.parse(decodedContent);
    } catch (error) {
      const jsonError = error as Error;
      console.error('JSON解析错误:', jsonError);
      console.error('尝试解析的内容:', decodedContent);
      logger.error('Google Play引荐来源解密', 'Google Play引荐来源解密失败', new Error(`JSON解析失败: ${jsonError.message}`));
      return unknownData;
    }

    const { source } = utmContentJson;
    
    // 验证必要字段
    if (!source?.data || !source?.nonce) {
      logger.error('Google Play引荐来源解密', 'Google Play引荐来源解密失败', new Error('缺少必要的source.data或source.nonce字段'));
      return unknownData;
    }

    // 解密source.data
    const decryptedData = await decryptAESGCM(source.data, source.nonce, MATRIX_FACEBOOK_INSTALL_DECRYPT_SECRET);
    
    // 尝试解析解密后的数据
    try {
      const parsedData = JSON.parse(decryptedData) as FacebookDecryptedData;
      console.log('解密后的数据:', parsedData);
      return {
        source:'ad',
        ad_channel:'meta',
        ad_info: decryptedData,
      }      
    } catch (error) {
      console.error('解密后的数据解析失败:', error);
            
      return unknownData; // 如果解析失败，返回原始解密字符串
    }
  } catch (error) {
    
    console.error('解密失败:', error);
    // 添加更多错误信息
    if (error instanceof Error) {
      console.error('错误详情:', {
        message: error.message,
        stack: error.stack,
        utmParams: utmParamsString
      });
    }
    logger.error('Google Play引荐来源解密', 'Google Play引荐来源解密失败', error);
    return unknownData;
  }
}
